import { useEffect, useState } from "react";
import { getLoginCookieAndSession } from "../utils";
import { getStripeConnectAccountStatus } from "../pages/Seller/seller-setup-api";
import { StripeAccountStatusCode } from "../pages/Seller/seller-setup-types";

// How often to re-check while verification has been submitted but Stripe hasn't approved it yet.
const PENDING_POLL_INTERVAL_MS = 3 * 60 * 1000;

// Fetches the current logged-in user's Stripe connected-account status.
// Used to decide whether "sell" CTAs should route to /sell (approved) or /presell (not yet approved),
// and whether to show the "verification pending" message vs. the "not started" empty state.
// While verification is pending, polls every few minutes so the UI updates once Stripe approves —
// polling stops as soon as the account is approved (or if verification was never started).
export const useStripeAccountStatus = () => {
  const [status, setStatus] = useState<StripeAccountStatusCode | null>(null);
  // True once a Stripe Connect account exists (user has submitted onboarding), regardless of its outcome.
  const [hasStartedVerification, setHasStartedVerification] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const profileId = getLoginCookieAndSession();
    if (!profileId) {
      setStatus(null);
      setHasStartedVerification(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchStatus = (isInitial: boolean) => {
      if (isInitial) setLoading(true);
      getStripeConnectAccountStatus(profileId)
        .then((result) => {
          if (cancelled) return;
          // getStripeConnectAccountStatus returns null (404) when no Stripe account exists yet.
          const started = !!result?.stripeAccountId;
          const nextStatus = result?.status ?? "PENDING";
          setHasStartedVerification(started);
          setStatus(nextStatus);

          // Only keep polling while there's an outstanding review to wait on.
          if (started && nextStatus !== "ACTIVE") {
            timeoutId = setTimeout(
              () => fetchStatus(false),
              PENDING_POLL_INTERVAL_MS
            );
          }
        })
        .catch(() => {
          if (!cancelled) setStatus("PENDING");
        })
        .finally(() => {
          if (!cancelled && isInitial) setLoading(false);
        });
    };

    fetchStatus(true);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const isApproved = status === "ACTIVE";
  const needsAction = status === "ACTION_REQUIRED" || status === "RESTRICTED";
  const isVerificationPending =
    hasStartedVerification && !isApproved && !needsAction;
  const sellRoute = isApproved ? "/sell" : "/presell";

  return {
    status,
    loading,
    isApproved,
    isVerificationPending,
    needsAction,
    sellRoute,
  };
};
